import React from "react";
import { DataTable, Badge } from "@/components/ui";
import { fmt, fmtDate } from "@/lib/formatters";
import { Eye, Edit2, UserX, CreditCard } from "lucide-react";

export const ProjectGeneralTable = ({ expenses, onView, onEdit, onToggleDisable, onDisburse, canDisburseExpenses, hideProjectColumns }) => {
  const allColumns = [
    /*
    {
      header: "Expense ID",
      accessor: (row) => (
        <div className="text-brand-orange font-bold !text-[10px] flex items-center py-2 uppercase tracking-tight">
          #{row.expenseId || "—"}
        </div>
      ),
      minWidth: "110px",
    },
    */
    {
      header: "Verve ID",
      accessor: (row) => {
        if (row.category === "general" || row.relatedToProject === false || String(row.relatedToProject).toLowerCase() === 'no') {
          return <span className="text-slate-400">—</span>;
        }
        const val = row.projectCustomId || row.project?.customId || row.project?.projectId || "—";
        return (
          <span className="text-[10px] uppercase tracking-tight whitespace-nowrap" title={val}>
            {val}
          </span>
        );
      },
      minWidth: "100px",
    },
    {
      header: 'Customer Info',
      accessor: (row) => {
        if (row.category === "general" || row.relatedToProject === false || String(row.relatedToProject).toLowerCase() === 'no') {
          return <span className="text-slate-400">—</span>;
        }

        const p = row.project || {};
        const fullName = p.customerName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || '—';
        const phone = p.phoneNumber || p.phone || '—';
        const email = p.email || '';
        const contactStr = email ? `${phone} | ${email}` : phone;

        return (
          <div className="flex flex-col min-w-0" title={`${fullName}\n${contactStr}`}>
            <span className="text-brand-dark tracking-tight text-sm truncate" title={fullName}>{fullName}</span>
            <span className="text-[10px] text-slate-400 truncate mt-0.5" title={contactStr}>
              {phone}{email && <span className="mx-1 text-slate-300">|</span>}{email}
            </span>
          </div>
        );
      },
      minWidth: '200px',
    },
    {
      header: 'Address',
      accessor: (row) => {
        if (row.category === "general" || row.relatedToProject === false || String(row.relatedToProject).toLowerCase() === 'no') {
          return <span className="text-slate-400">—</span>;
        }

        const p = row.project || {};
        const parts = [
          p.addressLine1 || p.address1,
          p.addressLine2 || p.address2,
          p.city,
          p.district,
          p.state,
          p.pincode
        ].filter(Boolean);
        const addressText = parts.length > 0 ? parts.join(", ") : '—';
        return (
          <div className="flex flex-col min-w-0" title={addressText}>
            <span className="text-slate-600 text-[11px] truncate">{addressText}</span>
          </div>
        );
      },
      minWidth: '200px',
    },
    {
      header: "Date",
      accessor: (row) => (
        <div className="text-slate-600 !text-[10px] flex items-center" title={fmtDate(row.date)}>
          {fmtDate(row.date)}
        </div>
      ),
      minWidth: "90px",
    },
    {
      header: "Category",
      accessor: (row) => {
        const catText = row.category === "advance"
          ? "Advance to Customer"
          : (row.relatedToProject === true || String(row.relatedToProject).toLowerCase() === 'yes')
            ? "Project Exp."
            : "General Exp.";
        return (
          <span
            title={catText}
            className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${row.category === "advance"
                ? 'text-amber-700 bg-amber-50 border border-amber-100'
                : (row.relatedToProject === true || String(row.relatedToProject).toLowerCase() === 'yes')
                  ? 'text-brand-blue bg-blue-50 border border-blue-100'
                  : 'text-emerald-700 bg-emerald-50 border border-emerald-100'
              }`}>
            {catText}
          </span>
        );
      },
      minWidth: "120px",
    },
    {
      header: "Requested By",
      accessor: (row) => (
        <div className="text-brand-dark !text-[10px] font-medium flex items-center truncate max-w-[130px]" title={row.requestedBy?.name || row.byWhomPaid}>
          {row.requestedBy?.name || row.byWhomPaid || "—"}
        </div>
      ),
      minWidth: "130px",
    },
    {
      header: "To Whom",
      accessor: (row) => (
        <div className="text-slate-500 !text-[10px] font-medium flex items-center truncate max-w-[130px]" title={row.toWhomPaid}>
          {row.toWhomPaid || "—"}
        </div>
      ),
      minWidth: "130px",
    },
    {
      header: "Reason",
      accessor: (row) => (
        <div className="text-slate-700 !text-[10px] font-medium flex items-center truncate max-w-[150px]" title={row.reason || row.materialName}>
          {row.reason || row.materialName || "—"}
        </div>
      ),
      minWidth: "150px",
    },
    {
      header: "Notes",
      accessor: (row) => (
        <div className="text-slate-500 !text-[10px] font-medium flex items-center truncate max-w-[150px]" title={row.notes}>
          {row.notes || "—"}
        </div>
      ),
      minWidth: "150px",
    },
    {
      header: "Owner Feedback",
      accessor: (row) => (
        <div className="text-indigo-600 !text-[10px] font-bold flex items-center truncate max-w-[120px]" title={row.ownerMessage}>
          {row.ownerMessage || "—"}
        </div>
      ),
      minWidth: "120px",
    },
    {
      header: "Amount",
      accessor: (row) => (
        <div className="flex flex-col gap-0.5">
          <div className="text-slate-500 !text-[10px] font-medium flex items-center gap-1">
            Req: <span className="text-slate-700 font-bold">₹{fmt(Math.abs(row.amount))}</span>
          </div>
          {['approved', 'partially_approved', 'paid'].includes(row.status) && (
            <div className="text-emerald-600 !text-[10px] font-medium flex items-center gap-1">
              Paid: <span className="font-bold">₹{fmt(Math.abs(row.approvedAmount || row.amount))}</span>
            </div>
          )}
        </div>
      ),
      minWidth: "100px",
    },
    {
      header: "Status",
      accessor: (row) => (
        <Badge status={row.status || "pending"} />
      ),
      minWidth: "100px",
    },
    {
      header: "Actions",
      accessor: (row) => {
        const isCancelled = row.status === 'cancelled';
        const isLocked = ['approved', 'partially_approved', 'paid', 'rejected'].includes(row.status);
        const cannotEdit = isCancelled || isLocked;
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); onView(row); }}
              className="p-1.5 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded-lg transition-all"
              title="View Detail"
            >
              <Eye size={16} />
            </button>
            <button
              disabled={cannotEdit}
              onClick={(e) => { e.stopPropagation(); onEdit(row); }}
              className={`p-1.5 text-slate-400 hover:text-emerald-500 hover:bg-emerald-50 rounded-lg transition-all ${cannotEdit ? "opacity-30 cursor-not-allowed" : ""}`}
              title={isLocked ? "Record is finalized and cannot be edited" : "Edit Record"}
            >
              <Edit2 size={16} />
            </button>
            {canDisburseExpenses && ['approved', 'partially_approved'].includes(row.status) && (
              <button
                onClick={(e) => { e.stopPropagation(); onDisburse(row); }}
                className="p-1.5 text-slate-400 hover:text-brand-orange hover:bg-orange-50 rounded-lg transition-all"
                title="Pay / Disburse Amount"
              >
                <CreditCard size={16} />
              </button>
            )}
          </div>
        );
      },
      minWidth: "60px",
    }
  ];

  const columns = hideProjectColumns
    ? allColumns.filter(c => !["Verve ID", "Customer Info", "Address"].includes(c.header))
    : allColumns;

  const getRowClassName = (row) => {
    return row.status === 'cancelled' ? "opacity-50 grayscale saturate-0" : "";
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-700">
      <DataTable
        columns={columns}
        data={expenses}
        getRowClassName={getRowClassName}
      />
    </div>
  );
};
